import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { provisioningFetch } from './ProvisioningGate';

const API_BASE = process.env.REACT_APP_API_BASE ?? '';
const POLL_INTERVAL_MS = 500;
const POLL_TIMEOUT_MS = 120_000;

interface TenantScanContext {
  tenantKey: string;
  displayName: string;
  visualId: string;
}

interface EventResult {
  receivedAt: string;
  rawPayload: string;
}

type Phase = 'waiting' | 'confirming' | 'pairing' | 'done' | 'error' | 'timeout';

/** Extracts the lockId from an Eloxx webhook payload: {"data":{"lockId":"..."},...} */
function tryParseLockId(rawPayload: string): string {
  try {
    const obj = JSON.parse(rawPayload) as Record<string, unknown>;
    const data = obj['data'] as Record<string, unknown> | undefined;
    const val = data?.['lockId'];
    return typeof val === 'string' ? val : '';
  } catch {
    return '';
  }
}

export default function LockerPairingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as { context?: TenantScanContext; after?: string } | null;
  const context = state?.context;
  const after = state?.after;

  const [phase, setPhase] = useState<Phase>('waiting');
  const [lockId, setLockId] = useState('');
  const [rawPayload, setRawPayload] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  type AssignWarning = 'visualId' | 'lockerId' | '';
  const [assignWarning, setAssignWarning] = useState<AssignWarning>('');

  // Helper to map backend error to user-friendly warning
  function getFriendlyError(msg: string): string {
    if (/visualid.+already assigned to another locker/i.test(msg)) {
      return 'This locker is already paired with a different lock.';
    }
    if (/lockerid.+already assigned to another visual/i.test(msg)) {
      return 'This lock is already paired with a different locker.';
    }
    return msg;
  }

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Redirect if landed without state (e.g. bookmarked directly).
  useEffect(() => {
    if (!context || !after) {
      navigate('/assign-lockers', { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for the Eloxx webhook event while in the 'waiting' phase.
  useEffect(() => {
    if (!context || !after || phase !== 'waiting') return;

    const poll = async () => {
      try {
        const url =
          `${API_BASE}/provision/tenants/${encodeURIComponent(context.tenantKey)}/events/latest` +
          `?after=${encodeURIComponent(after)}`;
        const resp = await provisioningFetch(url);

        if (resp.status === 204) return; // No new event yet — keep polling.
        if (!resp.ok) return;            // Transient error — keep polling.

        const ev: EventResult = await resp.json();
        clearInterval(pollRef.current!);
        clearTimeout(timeoutRef.current!);

        const parsedLockId = tryParseLockId(ev.rawPayload);
        setRawPayload(ev.rawPayload);
        setLockId(parsedLockId);

        // Pre-check for conflicts before showing the confirm UI
        if (parsedLockId) {
          try {
            const checkUrl =
              `${API_BASE}/provision/tenants/${encodeURIComponent(context.tenantKey)}/lockers/check` +
              `?visualId=${encodeURIComponent(context.visualId)}&lockId=${encodeURIComponent(parsedLockId)}`;
            const checkResp = await provisioningFetch(checkUrl);
            if (checkResp.ok) {
              const check = await checkResp.json() as { conflict?: string | null };
              if (check.conflict === 'visualId') setAssignWarning('visualId');
              else if (check.conflict === 'lockId') setAssignWarning('lockerId');
              else setAssignWarning('');
            }
          } catch {
            // Non-fatal — user can still confirm
          }
        }

        setPhase('confirming');
      } catch {
        // Network hiccup — keep polling.
      }
    };

    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
    timeoutRef.current = setTimeout(() => {
      clearInterval(pollRef.current!);
      setPhase('timeout');
    }, POLL_TIMEOUT_MS);

    return () => {
      clearInterval(pollRef.current!);
      clearTimeout(timeoutRef.current!);
    };
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmPairing = async () => {
    console.log('[PairLocker] confirmPairing called, lockId=', lockId, 'phase=', phase);
    if (!context || !lockId.trim()) return;
    setPhase('pairing');
    setAssignWarning('');
    try {
      const resp = await provisioningFetch(
        `${API_BASE}/provision/tenants/${encodeURIComponent(context.tenantKey)}/lockers`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ visualId: context.visualId, lockId: lockId.trim() }),
        }
      );
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as { conflict?: string; error?: string; message?: string };
        console.warn('[PairLocker] non-ok response', resp.status, body);
        if (body.conflict === 'visualId' || body.conflict === 'lockId') {
          setAssignWarning(body.conflict === 'visualId' ? 'visualId' : 'lockerId');
          setPhase('confirming');
          return;
        }
        const msg = body.error ?? body.message ?? `HTTP ${resp.status}`;
        throw new Error(msg);
      }
      console.log('[PairLocker] success', resp.status);
      setPhase('done');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Pairing failed.');
      setPhase('error');
    }
  };

  if (!context) return null;

  return (
    <div className="App PairPage">
      <header className="App-header">
        <h1>Pairing</h1>
        <div className="QrScannerCard PairCard">
          <div className="PairCardHeader">
            <p className="PairCardVisualId">{context.visualId}</p>
            <p className="PairCardTenant">{context.displayName}</p>
          </div>

          <div className="PairActionBox">

            {phase === 'waiting' && (
              <div className="PairWaiting">
                <span className="PairSpinner" aria-hidden="true" />
                <span className="PairWaitingText">Waiting for lock scan&hellip;</span>
              </div>
            )}

            {phase === 'confirming' && (
              <>
                <p className="QrStatus">Lock event received!</p>
                {assignWarning === 'visualId' && (
                  <p className="QrStatus" style={{ color: '#f4a030' }}>This locker is already paired with a different lock.</p>
                )}
                {assignWarning === 'lockerId' && (
                  <p className="QrStatus" style={{ color: '#f4a030' }}>This lock is already paired with a different locker.</p>
                )}
                {lockId
                  ? <p className="PairLockIdHint">Lock ID: {lockId}</p>
                  : <p className="QrHint">Lock ID could not be auto-detected.</p>
                }
                <div className="App-buttons">
                  <button className="App-button" onClick={confirmPairing} disabled={!lockId.trim()}>
                    Confirm pairing
                  </button>
                </div>
              </>
            )}

            {phase === 'pairing' && (
              <div className="PairWaiting">
                <span className="PairSpinner" aria-hidden="true" />
                <span className="PairWaitingText">Saving pairing&hellip;</span>
              </div>
            )}

            {phase === 'done' && (
              <>
                <p className="QrStatus">Locker {context.visualId} paired!</p>
                <div className="App-buttons">
                  <Link className="App-button" to="/assign-lockers">Pair another</Link>
                </div>
              </>
            )}

            {phase === 'timeout' && (
              <>
                <p className="QrStatus">Timed out — no lock event received within 2 minutes.</p>
                <div className="App-buttons">
                  <button className="App-button" onClick={() => setPhase('waiting')}>Retry</button>
                </div>
              </>
            )}

            {phase === 'error' && (
              <>
                <p className="QrStatus">{getFriendlyError(errorMsg)}</p>
                <div className="App-buttons">
                  <button className="App-button" onClick={() => setPhase('confirming')}>Try again</button>
                </div>
              </>
            )}


          </div>

          <div className="PairCancelRow">
              <Link className="App-button" to="/assign-lockers">Cancel</Link>
            </div>
        </div>
      </header>
    </div>
  );
}
